import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box} from "@mui/system";
import {Button, CircularProgress, Divider, IconButton, Typography} from "@mui/material";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import BasicModal from "../../common/BasicModal";
import BasicTable from "../../common/BasicTable";
import BasicTextField from "../../common/BasicTextField";
import styles from "../OrganizationSettings/organizationMainStyles";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

const tableStyles = {
    tableCell: {
        fontFamily: "LarsseitRegular",
        fontSize: "14px",
        fontWeight: 500,
        color: "#0C2728",
        padding: "8px 5px",
        whiteSpace: "normal",
        "&:first-of-type": {
            paddingLeft: "18px",
        },
        "&:last-of-type": {
            paddingRight: "18px",
        },
    },
    tableHead: {
        fontFamily: "LarsseitMedium",
    },
};

const paginationStyles = {
    bar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        pt: 1,
    },
    label: {
        fontFamily: "LarsseitMedium",
        fontSize: "14px",
        color: "#495D5E",
    },
};

const primaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    fontSize: "12px",
    bgcolor: "#003300",
    "&:hover": {
        bgcolor: "#004d00",
    },
    "&:disabled": {
        bgcolor: "rgba(0, 51, 0, 0.35)",
        color: "#FFFFFF",
    },
};

const secondaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    color: "#0C2728",
    borderColor: "#CED4D4",
};

function formatDate(value) {
    if (!value) {
        return "—";
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || _.get(error, "message") || "Something went wrong.";
    SnackBar.showMessage(message);
}

export default function TrendsOrganizations() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [searchString, setSearchString] = useState("");
    const [migrateConfirmTarget, setMigrateConfirmTarget] = useState(null);
    const [migratingOrgId, setMigratingOrgId] = useState(null);
    const loadRequestIdRef = useRef(0);

    const debouncedApplySearch = useMemo(
        () =>
            _.debounce((value) => {
                const trimmed = value.trim();
                setSearchString((current) => {
                    if (current === trimmed) {
                        return current;
                    }
                    setPage(0);
                    return trimmed;
                });
            }, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => debouncedApplySearch.cancel(), [debouncedApplySearch]);

    const load = useCallback(async () => {
        const requestId = ++loadRequestIdRef.current;
        setLoading(true);
        try {
            const params = {
                max: PAGE_SIZE + 1,
                offset: page * PAGE_SIZE,
                copiedToTeams: false,
                sort: "name",
                order: "asc",
            };
            const trimmedSearch = searchString.trim();
            if (trimmedSearch) {
                params.searchString = trimmedSearch;
            }
            const res = await organizationSettingsService.listTrendsOrganizations(params);
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            const raw = Array.isArray(res.data) ? res.data : [];
            if (raw.length === 0 && page > 0) {
                setPage(page - 1);
                return;
            }
            const hasMore = raw.length > PAGE_SIZE;
            setRows(raw.slice(0, PAGE_SIZE));
            setHasNextPage(hasMore);
            setHasLoadedOnce(true);
        } catch (e) {
            if (requestId !== loadRequestIdRef.current) {
                return;
            }
            setRows([]);
            setHasNextPage(false);
            handleRequestError(e);
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [page, searchString]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSearchField = useCallback(
        (event) => {
            const value = event.target.value;
            setSearchInput(value);
            debouncedApplySearch(value);
        },
        [debouncedApplySearch]
    );

    const clearSearch = useCallback(() => {
        if (!searchInput && !searchString) {
            return;
        }
        debouncedApplySearch.cancel();
        setSearchInput("");
        if (searchString) {
            setSearchString("");
            setPage(0);
        }
    }, [searchInput, searchString, debouncedApplySearch]);

    const isSearchPending = searchInput.trim() !== searchString;
    const isRefreshing = loading || isSearchPending;

    const handlePrevPage = useCallback(() => {
        if (page > 0 && !loading) {
            setPage(page - 1);
        }
    }, [page, loading]);

    const handleNextPage = useCallback(() => {
        if (hasNextPage && !loading) {
            setPage(page + 1);
        }
    }, [hasNextPage, loading, page]);

    const openMigrateConfirm = useCallback((row) => {
        setMigrateConfirmTarget({
            id: row.id,
            name: row.name,
        });
    }, []);

    const closeMigrateConfirm = useCallback(() => {
        if (migratingOrgId == null) {
            setMigrateConfirmTarget(null);
        }
    }, [migratingOrgId]);

    const executeMigrate = useCallback(async (organizationTrendsId) => {
        setMigratingOrgId(organizationTrendsId);
        try {
            await organizationSettingsService.migrateTrendsOrganizationToTeams(organizationTrendsId);
            SnackBar.showMessage("Organization migrated to Teams.");
            setMigrateConfirmTarget(null);
            await load();
            return true;
        } catch (e) {
            handleRequestError(e);
            return false;
        } finally {
            setMigratingOrgId(null);
        }
    }, [load]);

    const confirmMigrate = useCallback(async () => {
        if (migrateConfirmTarget?.id == null) {
            return;
        }
        await executeMigrate(migrateConfirmTarget.id);
    }, [migrateConfirmTarget, executeMigrate]);

    const tableColumns = useMemo(
        () => [
            {id: "id", label: "Trends ID", width: "100px"},
            {id: "name", label: "Name", minWidth: "200px"},
            {id: "dateCreated", label: "Created", width: "200px"},
            {id: "actions", label: "Actions", align: "left", width: "180px"},
        ],
        []
    );

    const tableRows = useMemo(
        () =>
            rows.map((row) => ({
                id: row.id ?? "—",
                name: row.name ?? "—",
                dateCreated: formatDate(row.dateCreated),
                actions: (
                    <Button
                        variant="contained"
                        size="small"
                        disabled={row.id == null || migratingOrgId === row.id}
                        onClick={(e) => {
                            e.stopPropagation();
                            openMigrateConfirm(row);
                        }}
                        sx={primaryButtonSx}
                    >
                        {migratingOrgId === row.id ? "Migrating…" : "Migrate to Teams"}
                    </Button>
                ),
            })),
        [rows, migratingOrgId, openMigrateConfirm]
    );

    const loadingBody = (
        <Box sx={styles.bodyLoading}>
            <CircularProgress sx={styles.bodyLoadingSpinner} />
        </Box>
    );

    const searchEndIcon = isRefreshing ? (
        <CircularProgress size={18} sx={searchStyles.spinner} />
    ) : searchInput ? (
        <ClearIcon sx={searchStyles.clearIcon} />
    ) : null;

    return (
        <Box sx={styles.root}>
            <Box sx={styles.container}>
                <Box sx={panelStyles.wrapper}>
                <Box sx={styles.sectionWrapper}>
                    <Box sx={headerStyles.row}>
                        <Box component="h2" sx={{...styles.heading, mb: 0}}>
                            Organizations in Trends Ready to Migrate
                        </Box>
                        <Box sx={searchStyles.container}>
                            <BasicTextField
                                name="organizationSearch"
                                value={searchInput}
                                startIcon={<SearchIcon sx={searchStyles.icon} />}
                                endIcon={searchEndIcon}
                                placeholder="Search by organization name"
                                handleChange={handleSearchField}
                                handleIconAction={isRefreshing ? undefined : clearSearch}
                                sx={searchStyles.textField}
                            />
                        </Box>
                    </Box>
                    <Divider orientation="horizontal" flexItem sx={{...styles.sectionDivider, mt: 2}} />
                    {loading && !hasLoadedOnce ? (
                        loadingBody
                    ) : tableRows.length === 0 && !isRefreshing ? (
                        <Typography sx={{...styles.infoItem, mt: 2}}>
                            {searchString.trim() ? "No organizations match your search." : "No organizations found."}
                        </Typography>
                    ) : (
                        <Box sx={{position: "relative", mt: 2}}>
                            {isRefreshing ? (
                                <Box sx={tableOverlayStyles.bar}>
                                    <CircularProgress size={22} sx={{color: "#003300"}} />
                                </Box>
                            ) : null}
                            <Box sx={{opacity: isRefreshing ? 0.55 : 1, transition: "opacity 0.2s ease"}}>
                                <BasicTable
                                    rows={tableRows}
                                    columns={tableColumns}
                                    withPagination={false}
                                    paperStyle={{
                                        borderRadius: "5px",
                                        boxShadow: "none",
                                        border: "1px solid #EEEFEF",
                                    }}
                                    tableCellStyle={tableStyles.tableCell}
                                    tableHeadStyle={tableStyles.tableHead}
                                />
                                <Box sx={paginationStyles.bar}>
                                    <IconButton
                                        aria-label="Previous page"
                                        size="small"
                                        onClick={handlePrevPage}
                                        disabled={page === 0 || isRefreshing}
                                    >
                                        <KeyboardArrowLeftIcon fontSize="small" />
                                    </IconButton>
                                    <Typography sx={paginationStyles.label}>Page {page + 1}</Typography>
                                    <IconButton
                                        aria-label="Next page"
                                        size="small"
                                        onClick={handleNextPage}
                                        disabled={!hasNextPage || isRefreshing}
                                    >
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
                </Box>
            </Box>
            <BasicModal
                open={migrateConfirmTarget != null}
                handleClose={closeMigrateConfirm}
                title="Migrate organization to Teams?"
                style={{width: "440px", maxWidth: "calc(100vw - 32px)"}}
            >
                <Box sx={{display: "flex", flexDirection: "column", pt: 1}}>
                    <Typography sx={{fontFamily: "LarsseitRegular", fontSize: "0.875rem", color: "#0C2728"}}>
                        This will copy the organization, its vertical, and users from Trends into Teams.
                    </Typography>
                    <Box
                        sx={{
                            my: 2,
                            py: 1.25,
                            px: 1.5,
                            borderRadius: "5px",
                            border: "1px solid #EEEFEF",
                            bgcolor: "#F8F9F9",
                        }}
                    >
                        <Typography
                            component="div"
                            sx={{fontFamily: "LarsseitMedium", fontSize: "0.875rem", color: "#0C2728", lineHeight: 1.5}}
                        >
                            Trends ID {migrateConfirmTarget?.id}
                        </Typography>
                        {migrateConfirmTarget?.name ? (
                            <Typography
                                component="div"
                                sx={{
                                    fontFamily: "LarsseitRegular",
                                    fontSize: "0.875rem",
                                    color: "#0C2728",
                                    lineHeight: 1.5,
                                    mt: 0.75,
                                }}
                            >
                                {migrateConfirmTarget.name}
                            </Typography>
                        ) : null}
                    </Box>
                    <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1, mt: 1}}>
                        <Button
                            variant="outlined"
                            onClick={closeMigrateConfirm}
                            disabled={migratingOrgId != null}
                            sx={secondaryButtonSx}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={confirmMigrate}
                            disabled={migratingOrgId != null}
                            sx={primaryButtonSx}
                        >
                            {migratingOrgId != null ? "Migrating…" : "Migrate to Teams"}
                        </Button>
                    </Box>
                </Box>
            </BasicModal>
        </Box>
    );
}

const panelStyles = {
    wrapper: {
        maxWidth: 1440,
        width: "100%",
        mx: "auto",
    },
};

const headerStyles = {
    row: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
    },
};

const searchStyles = {
    container: {
        flex: "1 1 240px",
        maxWidth: 360,
        minWidth: 220,
    },
    textField: {
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        height: "40px",
    },
    icon: {
        color: "#687979",
        fontSize: "20px",
    },
    clearIcon: {
        color: "#687979",
        fontSize: "18px",
    },
    spinner: {
        color: "#003300",
    },
};

const tableOverlayStyles = {
    bar: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 48,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
        pointerEvents: "none",
    },
};
